import { useEnum } from "@/api/services/global/enum";
import { Button } from "@/components/elements/Button";
import { Table } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { usePagination } from "@/hooks/usePagination";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Typography, ToggleButton } from "@mui/material";
import { StyledToggleButtonGroup } from "@/components/elements/ToggleButtonGroup/StyledToggleButtonGroup";
import { MRT_ColumnDef } from "material-react-table";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { truncateWithEllipsis } from "@/utils/string";
import { InnerBox } from "@/components/elements/InnerBox";
import { NewPagination } from "@/components/elements/Pagination/NewPagination";
import { formatDate } from "@/utils/datetime";
import { ImportPartnerUserModal } from "@/features/rso/components/ImportPartnerUserModal";
import { useVisible } from "@/hooks/useVisible";
import { IconTrash } from "@/assets/icons/IconTrash";
import { useDialog } from "@/hooks/useDialog";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { IconPlus } from "@/assets/icons";
import { deleteCompanyUserApi, useCompanyUserListDetail } from "@/api/services/admin/company/user";
import { TGetCompanyUserListDto } from "@/api/services/admin/company/user/dtos/get-user-list.dto";

type TCompanyUserListDetailProps = {
  isDeleted?: boolean;
};

export const CompanyUserListDetail: FC<TCompanyUserListDetailProps> = ({ isDeleted }) => {
  const { data: userActiveStatuses } = useEnum("EUserActiveStatus");
  const { id } = useParams();
  const { page: currentPage } = usePagination();
  const navigate = useNavigate();
  const location = useLocation();
  const [userTypeFilter, setUserTypeFilter] = useState<boolean>(true); // true = 顧客ユーザー, false = irohana担当者
  const { data: companyUserListResult, isLoading } = useCompanyUserListDetail(userTypeFilter);
  const companyUsers = companyUserListResult?.data ?? [];
  const confirmDelete = useDialog();
  const queryClient = useQueryClient();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  // Track previous page to detect page change
  const prevPageRef = useRef(currentPage);

  const columns = useMemo<MRT_ColumnDef<TGetCompanyUserListDto>[]>(
    () => [
      {
        header: "ユーザー名",
        id: "fullName",
        Cell: ({ row }) => (row.original.fullName ? truncateWithEllipsis(row.original.fullName, 30) : EMPTY_STR.TEXT),
        size: 120,
        muiTableHeadCellProps: {
          sx: {
            maxWidth: 120,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            maxWidth: 120,
          },
        },
      },
      {
        header: "メールアドレス",
        id: "email",
        accessorKey: "email",
        Cell: ({ row }) => (row.original.email ? truncateWithEllipsis(row.original.email, 18) : EMPTY_STR.TEXT),
      },
      {
        header: "所属",
        size: 120,
        Cell: ({ row }) => (row.original.isUser ? "顧客ユーザー" : "irohana担当者"),
      },
      {
        header: "ステータス",
        size: 120,
        Cell: ({ row }) => (row.original.activeStatus ? findLabelByValue(userActiveStatuses, row.original.activeStatus) : EMPTY_STR.TEXT),
      },
      {
        header: "登録日",
        size: 120,
        Cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        header: "最終ログイン日",
        size: 120,
        Cell: ({ row }) => formatDate(row.original.lastLoginTime),
      },
      {
        header: "",
        id: "action",
        Cell: ({ row }) => {
          return (
            <Button component={Link} size="sm" variant="outline" to={getDynamicRoute(AppRoutes.companyUserEdit, { id, userId: row.original.id })}>
              編集
            </Button>
          );
        },
        size: 120,
        muiTableBodyCellProps: {
          sx: {
            textAlign: "center",
          },
        },
      },
    ],
    [userActiveStatuses],
  );

  const matchUserModal = useVisible();

  const { table } = useTable({
    columns,
    rows: companyUsers,
    enableRowSelection: true,
    pinningColumns: ["action"],
  });

  // Get selected IDs from current page
  const selectedIds = React.useMemo(() => table.getSelectedRowModel().rows.map((row) => row.original.id), [table.getSelectedRowModel()]);

  // Reset selection when page changes
  useEffect(() => {
    if (prevPageRef.current !== currentPage) {
      table.resetRowSelection();
      prevPageRef.current = currentPage;
    }
  }, [currentPage]);

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!id) return;
    setIsSubmit(true);
    try {
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteCompanyUserApi(Number(id), selectedIds);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      table.resetRowSelection();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_LIST] });
    } catch (error) {
      showError(error);
    }
    setIsSubmit(false);
  };

  const onCancelConfirm = () => {
    setIsSubmit(false);
    confirmDelete.cancel();
  };

  const handleUserTypeChange = (_event: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
    if (newValue !== null) {
      table.resetRowSelection();
      setUserTypeFilter(newValue);
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("page");
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  };

  const getTitle = () => {
    if (userTypeFilter === true) {
      return "顧客ユーザーの一覧";
    }
    return "irohana担当者の一覧";
  };

  return (
    <InnerBox>
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="sub16Bold" sx={{ minWidth: "200px", whiteSpace: "nowrap" }}>
              {getTitle()} ({companyUsers?.length ?? 0})
            </Typography>
            <StyledToggleButtonGroup value={userTypeFilter} exclusive onChange={handleUserTypeChange} size="small">
              <ToggleButton value={true}>顧客ユーザー</ToggleButton>
              <ToggleButton value={false}>irohana担当者</ToggleButton>
            </StyledToggleButtonGroup>
          </Stack>
          {!isDeleted && (
            <Stack direction="row" sx={{ gap: "20px" }}>
              <Button component={Link} to={getDynamicRoute(AppRoutes.companyUserCreate, { id })} size="sm" variant="outline" startIcon={<IconPlus />}>
                ユーザー追加
              </Button>
            </Stack>
          )}
        </Stack>
        <Table table={table} isLoading={isLoading} />
        <Stack direction="row" justifyContent="space-between">
          <NewPagination totalPage={companyUserListResult?.pagination?.totalPage} />
          {!isDeleted && (
            <Button
              size="sm"
              variant={"danger"}
              disabled={selectedIds.length === 0 || isSubmit}
              startIcon={<IconTrash />}
              sx={{
                width: "88px",
              }}
              onClick={handleDelete}
            >
              削除
            </Button>
          )}
        </Stack>
      </Stack>
      <ImportPartnerUserModal modal={matchUserModal} />
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onClose={onCancelConfirm}
        onOk={confirmDelete.confirm}
        message="選択したユーザーを削除してもよろしいですか？"
      />
    </InnerBox>
  );
};
